import { createContext, useContext, useEffect, useState, ReactNode } from "react";

type Theme = "dark" | "light";

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Bump this to move every returning visitor to the default once; their next
// choice on the toggle sticks after that. Default is light (paper) since
// 2026-09-20; dark stays one click away.
const THEME_VERSION = "4";

export function ThemeProvider({ children }: { children: ReactNode }) {
  // Initialize theme synchronously from localStorage to prevent flash
  const [theme, setTheme] = useState<Theme>(() => {
    if (typeof window !== 'undefined') {
      const storedVersion = localStorage.getItem("theme-version");
      if (storedVersion === THEME_VERSION) {
        const stored = localStorage.getItem("theme") as Theme;
        if (stored === "light" || stored === "dark") {
          return stored;
        }
      }
    }
    return "light";
  });

  // Apply theme class + data-theme attribute to document immediately and on changes
  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove("dark", "light");
    root.classList.add(theme);
    root.setAttribute("data-theme", theme);
    localStorage.setItem("theme", theme);
    localStorage.setItem("theme-version", THEME_VERSION);
  }, [theme]);

  const toggleTheme = () => {
    setTheme((prev) => (prev === "dark" ? "light" : "dark"));
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}
